/**
 * MIS STICKERS COLECCIONADOS (screen #29 `mis_stickers_coleccionados`): álbum de
 * paquetes con progreso por paquete (poseídos / total) + favoritos recientes.
 *
 * Join reactivo catálogo [H] (`sticker_pack`/`sticker`) × poseídos [L]
 * (`user_sticker`), ambos en la DB local de PowerSync. El progreso por paquete
 * es la función PURA `computeStickerPackProgress`. UI-vars + lucide.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Award, Sparkles, Star, Sticker as StickerIcon } from "lucide-solid";
import {
  computeStickerPackProgress,
  getPackOwnedCountsQuery,
  getStickerPacksQuery,
  getUserStickersQuery,
  queryKeys,
} from "@famidex/shared";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

export function StickersPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const { userId } = session;

  const enabled = () => Boolean(drizzle() && userId());

  const packs = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()),
    queryKey: () => queryKeys.gamification.stickerPacks(),
    queryFn: () => {
      const d = drizzle();
      return d ? getStickerPacksQuery(d) : null;
    },
  });

  const ownedCounts = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.gamification.packProgress(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getPackOwnedCountsQuery(d, uid) : null;
    },
  });

  const userStickers = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.gamification.userStickers(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getUserStickersQuery(d, uid) : null;
    },
  });

  const ownedByPack = createMemo(() => {
    const map = new Map<string, number>();
    for (const row of ownedCounts.data ?? []) {
      if (row.pack_id) {
        map.set(row.pack_id, row.owned);
      }
    }
    return map;
  });

  const packsWithProgress = createMemo(() =>
    (packs.data ?? []).map((pack) => ({
      pack,
      progress: computeStickerPackProgress(ownedByPack().get(pack.id) ?? 0, pack.total_items ?? 0),
    })),
  );

  const favorites = createMemo(() => (userStickers.data ?? []).filter((s) => s.is_favorite));

  return (
    <div class="flex flex-col gap-8" data-testid="stickers-page">
      <header>
        <h1 class="text-2xl font-semibold text-foreground">{t(msg`Mis stickers`)}</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          {t(msg`Completa los álbumes de temporada coleccionando stickers exclusivos.`)}
        </p>
      </header>

      {/* Paquetes */}
      <section class="flex flex-col gap-4">
        <h2 class="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {t(msg`Paquetes de stickers`)}
        </h2>
        <Show
          when={packsWithProgress().length > 0}
          fallback={
            <div class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-12 text-center text-muted-foreground">
              <StickerIcon class="h-8 w-8" />
              <p class="text-sm">{t(msg`Aún no hay paquetes disponibles.`)}</p>
            </div>
          }
        >
          <ul class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <For each={packsWithProgress()}>
              {({ pack, progress }) => (
                <li
                  data-testid="sticker-pack"
                  class="flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card"
                >
                  <div class="relative aspect-[3/2] bg-muted">
                    <Show
                      when={pack.image_url}
                      fallback={
                        <div class="grid h-full w-full place-items-center text-muted-foreground/40">
                          <StickerIcon class="h-10 w-10" />
                        </div>
                      }
                    >
                      <img
                        src={pack.image_url ?? ""}
                        alt={pack.name ?? ""}
                        loading="lazy"
                        class="h-full w-full object-cover"
                      />
                    </Show>
                    <Show when={progress.complete}>
                      <span class="absolute right-2 top-2 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
                        {t(msg`¡Completo!`)}
                      </span>
                    </Show>
                  </div>
                  <div class="flex flex-1 flex-col p-4">
                    <h3 class="text-sm font-semibold text-foreground">
                      {pack.name ?? t(msg`Paquete`)}
                    </h3>
                    <Show when={pack.season}>
                      <p class="text-xs text-muted-foreground">{pack.season}</p>
                    </Show>
                    <div class="mt-auto pt-4">
                      <div class="mb-1 flex items-center justify-between text-xs">
                        <span class="font-semibold text-primary">
                          {progress.owned} / {progress.total || "?"} {t(msg`stickers`)}
                        </span>
                        <span class="flex items-center gap-1 text-muted-foreground">
                          <Show when={progress.complete}>
                            <Sparkles class="h-3.5 w-3.5 text-primary" />
                          </Show>
                          {progress.percent}%
                        </span>
                      </div>
                      <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          class="h-full rounded-full bg-primary transition-[width] duration-500"
                          style={{ width: `${progress.percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </section>

      {/* Favoritos */}
      <Show when={favorites().length > 0}>
        <section class="flex flex-col gap-4">
          <h2 class="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-muted-foreground">
            <Star class="h-4 w-4 text-warning" />
            {t(msg`Favoritos`)}
          </h2>
          <ul class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <For each={favorites()}>
              {(sticker) => (
                <li
                  data-testid="favorite-sticker"
                  class="relative grid aspect-square place-items-center rounded-2xl border-2 border-dashed border-border/70 bg-secondary p-3"
                >
                  <Show
                    when={sticker.image_url}
                    fallback={<Award class="h-8 w-8 text-muted-foreground/40" />}
                  >
                    <img
                      src={sticker.image_url ?? ""}
                      alt={sticker.name ?? ""}
                      loading="lazy"
                      class="h-3/4 w-3/4 object-contain"
                    />
                  </Show>
                  <Star class="absolute bottom-2 right-2 h-4 w-4 fill-warning text-warning" />
                </li>
              )}
            </For>
          </ul>
        </section>
      </Show>
    </div>
  );
}
